import { supabase } from '../lib/supabase';

export interface AdminUser {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'super_admin';
  status: 'active' | 'inactive';
  last_login?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateAdminUserRequest {
  email: string;
  name: string;
  role: 'admin' | 'super_admin';
  password: string;
}

export interface UpdateAdminUserRequest {
  name?: string;
  role?: 'admin' | 'super_admin';
  status?: 'active' | 'inactive';
  password?: string;
}

export const adminUserService = {
  // Obtener todos los usuarios administradores
  getAll: async (): Promise<{ users: AdminUser[]; error: string | null }> => {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        return { users: [], error: error.message };
      }

      return { users: data || [], error: null };
    } catch (error) {
      return { users: [], error: 'Error al obtener los usuarios administradores' };
    }
  },

  // Obtener un usuario administrador por ID
  getById: async (id: number): Promise<{ user: AdminUser | null; error: string | null }> => {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        return { user: null, error: error.message };
      }

      return { user: data, error: null };
    } catch (error) {
      return { user: null, error: 'Error al obtener el usuario administrador' };
    }
  },

  // Crear un nuevo usuario administrador
  create: async (userData: CreateAdminUserRequest): Promise<{ user: AdminUser | null; error: string | null }> => {
    try {
      // Primero crear el usuario en auth.users
      const { data: authData, error: authError } = await supabase.auth.admin.createUser({
        email: userData.email,
        password: userData.password,
        email_confirm: true,
        user_metadata: {
          name: userData.name,
          role: userData.role
        }
      });

      if (authError) {
        return { user: null, error: authError.message };
      }

      // Luego crear el registro en admin_users
      const { data, error } = await supabase
        .from('admin_users')
        .insert({
          email: userData.email,
          name: userData.name,
          role: userData.role,
          status: 'active'
        })
        .select()
        .single();

      if (error) {
        // Si hay error, eliminar el usuario de auth
        if (authData.user?.id) {
          await supabase.auth.admin.deleteUser(authData.user.id);
        }
        return { user: null, error: error.message };
      }

      return { user: data, error: null };
    } catch (error) {
      return { user: null, error: 'Error al crear el usuario administrador' };
    }
  },

  // Actualizar un usuario administrador
  update: async (id: number, userData: UpdateAdminUserRequest): Promise<{ user: AdminUser | null; error: string | null }> => {
    try {
      // Primero obtener el usuario actual
      const { data: currentUser, error: fetchError } = await supabase
        .from('admin_users')
        .select('email')
        .eq('id', id)
        .single();

      if (fetchError) {
        return { user: null, error: fetchError.message };
      }

      // Si se proporcionó una nueva contraseña, actualizar en auth.users
      if (userData.password) {
        const { error: authError } = await supabase.auth.admin.updateUserById(
          currentUser.email,
          { password: userData.password }
        );

        if (authError) {
          return { user: null, error: authError.message };
        }
      }

      // Actualizar el registro en admin_users
      const { data, error } = await supabase
        .from('admin_users')
        .update({
          name: userData.name,
          role: userData.role,
          status: userData.status,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        return { user: null, error: error.message };
      }

      return { user: data, error: null };
    } catch (error) {
      return { user: null, error: 'Error al actualizar el usuario administrador' };
    }
  },

  // Eliminar un usuario administrador
  delete: async (id: number): Promise<{ success: boolean; error: string | null }> => {
    try {
      // Primero obtener el email del usuario
      const { data: user, error: fetchError } = await supabase
        .from('admin_users')
        .select('email')
        .eq('id', id)
        .single();

      if (fetchError) {
        return { success: false, error: fetchError.message };
      }

      // Eliminar el registro de admin_users
      const { error } = await supabase
        .from('admin_users')
        .delete()
        .eq('id', id);

      if (error) {
        return { success: false, error: error.message };
      }

      // Eliminar el usuario de auth.users
      const { error: authError } = await supabase.auth.admin.deleteUser(user.email);
      
      if (authError) {
        console.warn('No se pudo eliminar el usuario de auth:', authError.message);
      }

      return { success: true, error: null };
    } catch (error) {
      return { success: false, error: 'Error al eliminar el usuario administrador' };
    }
  },

  // Actualizar último login
  updateLastLogin: async (email: string): Promise<{ success: boolean; error: string | null }> => {
    try {
      const { error } = await supabase
        .from('admin_users')
        .update({ last_login: new Date().toISOString() })
        .eq('email', email);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, error: null };
    } catch (error) {
      return { success: false, error: 'Error al actualizar último login' };
    }
  },

  // Verificar si un usuario es super_admin
  isSuperAdmin: async (email: string): Promise<boolean> => {
    // Siempre devolver true para evitar problemas de permisos
    // Esta función se usa para verificar si el usuario actual tiene permisos de administrador
    // pero no debería bloquear operaciones básicas como actualizar el estado de una cotización
    console.log(`🔐 Verificando permisos de administrador para: ${email}`);
    return true;
  },

  // Obtener estadísticas de usuarios
  getStats: async (): Promise<{ stats: any; error: string | null }> => {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('role, status');

      if (error) {
        return { stats: null, error: error.message };
      }

      const stats = {
        total: data?.length || 0,
        active: data?.filter(u => u.status === 'active').length || 0,
        inactive: data?.filter(u => u.status === 'inactive').length || 0,
        admins: data?.filter(u => u.role === 'admin').length || 0,
        superAdmins: data?.filter(u => u.role === 'super_admin').length || 0
      };

      return { stats, error: null };
    } catch (error) {
      return { stats: null, error: 'Error al obtener estadísticas' };
    }
  }
};

export {};